/** UI strings and locale routing.
 *
 * Page *copy* comes from the database and is edited by the owner in the
 * admin. Only chrome - nav labels, form fields, buttons, page meta - lives here.
 *
 * German is the canonical language; en/ja/ko/zh trees are machine-written and
 * type-checked against the German shape so no key can go missing.
 */

import type { Locale } from './api'

export const LOCALES: Locale[] = ['de', 'en', 'ja', 'ko', 'zh']
export const DEFAULT_LOCALE: Locale = 'de'

/** Autonyms for the language switcher - never translated. */
export const LANGUAGE_NAMES: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  ja: '日本語',
  ko: '한국어',
  zh: '简体中文',
}

/** BCP 47 tags for Intl date/number formatting. */
export const INTL_LOCALES: Record<Locale, string> = {
  de: 'de-DE',
  en: 'en-GB',
  ja: 'ja-JP',
  ko: 'ko-KR',
  zh: 'zh-CN',
}

/** German keeps the original site's unprefixed slugs. */
const DE_ROUTES = {
  home: '/',
  uebermich: '/uebermich',
  klang: '/klang',
  galerie: '/galerie',
  shop: '/shop',
  service: '/reparaturen',
  kontakt: '/kontakt',
  impressum: '/impressum',
  datenschutz: '/datenschutz',
} as const

export type PageKey = keyof typeof DE_ROUTES

/** Every non-German locale shares the English slug set under its /{locale}
 *  prefix - CJK words in URLs would only ever appear percent-encoded. The
 *  "galery" spelling is historical and kept for URL stability. */
const NON_DE_SLUGS: Record<PageKey, string> = {
  home: '',
  uebermich: 'about-me',
  klang: 'timbre',
  galerie: 'galery',
  shop: 'shop',
  service: 'repairs',
  kontakt: 'contact',
  impressum: 'legal-notice',
  datenschutz: 'dataprotection',
}

const PAGE_KEYS = Object.keys(DE_ROUTES) as PageKey[]

function buildRoutes(locale: Locale): Record<PageKey, string> {
  if (locale === 'de') return DE_ROUTES
  return Object.fromEntries(
    PAGE_KEYS.map((key) => [
      key,
      NON_DE_SLUGS[key] ? `/${locale}/${NON_DE_SLUGS[key]}` : `/${locale}`,
    ]),
  ) as Record<PageKey, string>
}

export const ROUTES = Object.fromEntries(
  LOCALES.map((locale) => [locale, buildRoutes(locale)]),
) as Record<Locale, Record<PageKey, string>>

export function pathFor(locale: Locale, page: PageKey): string {
  return ROUTES[locale][page]
}

export function productPath(locale: Locale, slug: string): string {
  return locale === 'de' ? `/shop/${slug}` : `/${locale}/shop/${slug}`
}

/** The same page in every locale - feeds the hreflang alternates. */
export function pagePaths(page: PageKey): Record<Locale, string> {
  return Object.fromEntries(
    LOCALES.map((locale) => [locale, ROUTES[locale][page]]),
  ) as Record<Locale, string>
}

export function productPaths(slug: string): Record<Locale, string> {
  return Object.fromEntries(
    LOCALES.map((locale) => [locale, productPath(locale, slug)]),
  ) as Record<Locale, string>
}

/** Derive the active locale from the URL. */
export function localeFromPath(pathname: string): Locale {
  for (const locale of LOCALES) {
    if (locale === 'de') continue
    if (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)) return locale
  }
  return 'de'
}

const PRODUCT_PATH_RE = new RegExp(
  `^(?:/(?:${LOCALES.filter((l) => l !== 'de').join('|')}))?/shop/(.+)$`,
)

// ---------------------------------------------------------------------------
// Browser-language detection. German stays the default: unprefixed URLs are
// German, and only a clear browser preference for another supported language
// redirects the very first visit. An explicit choice in the switcher is
// remembered and always wins over detection.
// ---------------------------------------------------------------------------

export const LOCALE_STORAGE_KEY = 'zvidori-locale'

/** Best supported locale for the browser's language list; 'de' if none match.
 *  Region subtags are ignored - zh-TW readers get Simplified rather than
 *  German, which is the lesser evil. */
export function detectLocale(languages: readonly string[]): Locale {
  for (const tag of languages) {
    const primary = tag.toLowerCase().split('-')[0]
    if ((LOCALES as string[]).includes(primary)) return primary as Locale
  }
  return DEFAULT_LOCALE
}

export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Storage may be unavailable (private mode) - detection still works.
  }
}

export function storedLocale(): Locale | null {
  try {
    const value = localStorage.getItem(LOCALE_STORAGE_KEY)
    return value && (LOCALES as string[]).includes(value) ? (value as Locale) : null
  } catch {
    return null
  }
}

/** Is this an exact German page or product URL that has an equivalent in
 *  other locales? (Auto-redirect must never fire on unknown/404 paths.) */
export function isLocalizablePath(pathname: string): boolean {
  return (
    (Object.values(ROUTES.de) as string[]).includes(pathname) ||
    PRODUCT_PATH_RE.test(pathname)
  )
}

/** The equivalent URL in another language, for the header switcher. */
export function switchLocalePath(pathname: string, target: Locale): string {
  const current = localeFromPath(pathname)
  if (current === target) return pathname

  for (const key of PAGE_KEYS) {
    if (ROUTES[current][key] === pathname) return ROUTES[target][key]
  }

  // Product detail pages keep their slug across locales.
  const productMatch = pathname.match(PRODUCT_PATH_RE)
  if (productMatch) return productPath(target, productMatch[1])

  return ROUTES[target].home
}

const de = {
  nav: {
    uebermich: 'Über mich',
    klang: 'Klang',
    galerie: 'Galerie',
    shop: 'Shop',
    service: 'Service',
    kontakt: 'Kontakt',
  },
  skipToContent: 'Zum Inhalt springen',
  menu: 'Menü',
  closeMenu: 'Menü schließen',
  language: 'Sprache',

  footer: {
    privacy: 'Datenschutz',
    imprint: 'Impressum',
    follow: 'Folgen',
  },

  /** The closing line of the Impressum: who made the site this one replaced.
   *  The name and link follow it - see pages/ContentPage.tsx. */
  credits: {
    originalSite: 'Konzeption und Gestaltung der ursprünglichen Webseite (2023 bis 2026)',
  },

  /** The Google-Analytics opt-in bar. `settings` doubles as the footer link
   *  that brings the bar back, so a decision can always be withdrawn. */
  consent: {
    title: 'Cookie-Einstellungen',
    body: 'Wir würden gern mit Google Analytics auswerten, wie diese Website genutzt wird. Dafür werden Cookies auf Ihrem Gerät gespeichert und Daten an Google übertragen - nur, wenn Sie zustimmen. Sie können Ihre Entscheidung jederzeit im Fußbereich der Seite ändern.',
    more: 'Datenschutzerklärung',
    accept: 'Akzeptieren',
    decline: 'Ablehnen',
    settings: 'Cookie-Einstellungen',
  },

  /** The workshop map on Kontakt. Nothing reaches Google until the visitor
   *  presses `load` - see components/LocationMap.tsx. */
  map: {
    title: 'Anfahrt',
    load: 'Karte laden',
    note: 'Die Karte wird von Google geladen. Dabei werden Ihre IP-Adresse und Angaben zu Ihrem Gerät an Google übertragen. Ohne Ihren Klick geschieht das nicht.',
    more: 'Datenschutzerklärung',
    directions: 'Route planen',
    frameTitle: 'Google-Maps-Karte: {address}',
  },

  shop: {
    title: 'Shop',
    eyebrow: 'Instrumente',
    intro:
      'Die Instrumente können nicht online gekauft werden. Senden Sie eine Anfrage und spielen Sie das Instrument in Ruhe in der Werkstatt an.',
    allCategories: 'Alle',
    empty: 'Zurzeit sind keine Instrumente eingestellt.',
    priceOnRequest: 'Preis auf Anfrage',
    available: 'Verfügbar',
    reserved: 'Reserviert',
    sold: 'Verkauft',
    details: 'Details ansehen',
    backToShop: 'Zurück zum Shop',
    requestToBuy: 'Kaufanfrage senden',
    notFound: 'Instrument nicht gefunden.',
    year: 'Jahr',
    model: 'Modell',
    category: 'Kategorie',
  },

  /** The closing band on the start page. */
  home: {
    invite: 'Kommen Sie in die Werkstatt und spielen Sie die Instrumente in Ruhe an.',
  },

  form: {
    heading: 'Schreiben Sie mir',
    name: 'Name',
    email: 'E-Mail-Adresse',
    phone: 'Telefonnummer',
    subject: 'Betreff',
    message: 'Nachricht',
    captcha: 'Bitte rechnen Sie',
    captchaHint: 'Eine kleine Rechenaufgabe schützt vor automatischen Spam-Nachrichten.',
    pickTimeFirst: 'Sie haben noch keine Uhrzeit gewählt. Bitte wählen Sie eine aus - oder blenden Sie die Terminauswahl aus.',
    send: 'Senden',
    sending: 'Wird gesendet …',
    required: 'Pflichtfeld',
    success: 'Vielen Dank! Ihre Anfrage ist bei uns eingegangen.',
    error: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    topicLegend: 'Worum geht es?',
    topicHint: 'Optional - hilft uns, Ihnen schneller zu antworten.',
    topics: {
      repair: 'Reparatur',
      appraisal: 'Gutachten & Versicherung',
      purchase: 'Kaufinteresse',
      other: 'Sonstiges',
    } as Record<string, string>,
    photos: 'Fotos anhängen (optional)',
    photosHint: 'Bis zu 3 Bilder - z. B. vom beschädigten Instrument oder Bogen.',
    photoAdd: 'Foto auswählen',
    photoUploading: 'Foto wird hochgeladen …',
    photoRemove: 'Foto entfernen',
    photoError: 'Das Bild konnte nicht hochgeladen werden.',
    scheduleToggle: 'Termin für einen Werkstattbesuch vereinbaren',
    scheduleToggleHint:
      'Optional - nur, wenn Sie vorbeikommen möchten. Eine einfache Frage können Sie auch ohne Termin senden.',
    scheduleHide: 'Doch kein Termin - ausblenden',
    preferredTimes: 'Wunschtermine',
    preferredTimesHint:
      'Wählen Sie bis zu {n} Wunschtermine - wir bestätigen einen davon per E-Mail.',
    pickSlotHint: 'Wählen Sie einen freien Termin - er wird sofort verbindlich gebucht.',
    removeTime: 'Entfernen',
    pickSlot: 'Freien Termin wählen',
    pickDayFirst: 'Bitte wählen Sie links einen Tag aus.',
    maxReached: 'Maximale Anzahl erreicht - tippen Sie einen Termin an, um ihn zu entfernen.',
    noSlots: 'An diesem Tag sind keine Termine verfügbar.',
    calendarUnavailable:
      'Der Terminkalender ist momentan nicht erreichbar. Bitte schlagen Sie stattdessen Wunschtermine vor.',
    loadingSlots: 'Freie Termine werden geladen …',
    suggestInstead: 'Kein passender Termin dabei? Wunschtermin vorschlagen',
    showLiveInstead: 'Zurück zu den freien Terminen',
    book: 'Termin verbindlich buchen',
    bookingSuccess: 'Vielen Dank! Ihr Termin ist bestätigt. Sie erhalten eine E-Mail.',
  },

  gallery: {
    close: 'Schließen',
    previous: 'Vorheriges Bild',
    next: 'Nächstes Bild',
    imageCounter: 'Bild {i} von {n}',
  },

  scheduler: {
    prevMonth: 'Voriger Monat',
    nextMonth: 'Nächster Monat',
  },

  wizard: {
    eyebrow: 'Kaufanfrage',
    steps: ['Wunschtermin', 'Ihre Daten', 'Absenden'] as readonly string[],
    stepOf: 'Schritt {i} von {n}',
    terminHint:
      'Möchten Sie das Instrument in der Werkstatt anspielen? Wählen Sie einen Tag und passende Uhrzeiten - oder überspringen Sie diesen Schritt einfach.',
    terminHintLive:
      'Wählen Sie einen freien Termin für Ihren Werkstattbesuch - er wird direkt für Sie reserviert.',
    skip: 'Ohne Terminwunsch fortfahren',
    contactHint: 'Wie dürfen wir Sie erreichen?',
    messageLabel: 'Ihre Nachricht (optional)',
    messagePlaceholder: 'Fragen zum Instrument, zum Preis, zur Anreise …',
    summaryHint: 'Bitte prüfen Sie Ihre Angaben - dann senden Sie die Anfrage ab.',
    summaryProduct: 'Instrument',
    summaryTimes: 'Wunschtermine',
    summarySlot: 'Gewählter Termin',
    summaryContact: 'Kontakt',
    summaryMessage: 'Nachricht',
    noTimes: 'Kein Terminwunsch - wir melden uns per E-Mail.',
    back: 'Zurück',
    next: 'Weiter',
    send: 'Anfrage absenden',
    book: 'Termin verbindlich buchen',
    contactInvalid: 'Bitte geben Sie Ihren Namen und eine gültige E-Mail-Adresse an.',
    successTitle: 'Vielen Dank!',
    close: 'Schließen',
  },

  notFound: {
    title: 'Seite nicht gefunden',
    body: 'Die aufgerufene Seite existiert nicht.',
    home: 'Zur Startseite',
  },
  loading: 'Wird geladen …',

  /** Page titles, meta descriptions and content-fallback headings - kept here
   *  so no page component needs a per-locale ternary. */
  meta: {
    home: {
      title: 'Zvi Dori | Geigenbaumeister',
      description:
        'Zvi Dori, Geigenbaumeister in Hannover. Neue Meisterinstrumente, Einzelanfertigungen nach alten Vorbildern sowie Barockinstrumente und Gamben.',
    },
    uebermich: {
      title: 'Über mich | zvidori',
      description:
        'Zvi Dori führt seit 2004 eine eigene Geigenbauwerkstatt in Hannover - Meister in der alten italienischen Tradition.',
      heading: 'Über mich',
    },
    klang: {
      title: 'Klang | zvidori',
      description:
        'Das Klangideal der alten italienischen Meister - Stradivari, Amati, Guarneri - leitet jeden Neubau aus der Werkstatt.',
      heading: 'Klang',
    },
    galerie: {
      title: 'Galerie | zvidori',
      description: 'Instrumente aus der Werkstatt von Zvi Dori, Geigenbaumeister in Hannover.',
    },
    shop: {
      description:
        'Meisterinstrumente, Violinen, Bratschen, Celli und Bögen aus der Werkstatt von Zvi Dori in Hannover.',
    },
    service: {
      title: 'Reparaturen & Service | zvidori',
      description:
        'Reparatur, Restaurierung, Bogenservice, Klangeinstellung und Gutachten für Streichinstrumente - Werkstatt von Zvi Dori, Geigenbaumeister in Hannover.',
      heading: 'Reparaturen & Service',
    },
    kontakt: {
      title: 'Kontakt | zvidori',
      description:
        'Vereinbaren Sie einen Termin in der Werkstatt von Zvi Dori, Geigenbaumeister in Hannover.',
      heading: 'Kontakt',
    },
  },
}

export type Strings = typeof de

const en: Strings = {
  nav: {
    uebermich: 'About me',
    klang: 'Timbre',
    galerie: 'Galery',
    shop: 'Shop',
    service: 'Services',
    kontakt: 'Contact',
  },
  skipToContent: 'Skip to content',
  menu: 'Menu',
  closeMenu: 'Close menu',
  language: 'Language',

  footer: {
    privacy: 'Data protection',
    imprint: 'Legal Notice',
    follow: 'Follow',
  },

  credits: {
    originalSite: 'Concept and design of the original website (2023 to 2026)',
  },

  consent: {
    title: 'Cookie settings',
    body: 'We would like to use Google Analytics to understand how this website is used. This stores cookies on your device and transfers data to Google - only if you agree. You can change your decision at any time in the footer of the page.',
    more: 'Privacy policy',
    accept: 'Accept',
    decline: 'Decline',
    settings: 'Cookie settings',
  },

  map: {
    title: 'How to find us',
    load: 'Load map',
    note: 'The map is loaded from Google. This transfers your IP address and details about your device to Google. Without your click, none of that happens.',
    more: 'Privacy policy',
    directions: 'Get directions',
    frameTitle: 'Google Maps: {address}',
  },

  shop: {
    title: 'Shop',
    eyebrow: 'Instruments',
    intro:
      'Instruments cannot be purchased online. Send an enquiry and play the instrument at your leisure in the workshop.',
    allCategories: 'All',
    empty: 'No instruments are listed at the moment.',
    priceOnRequest: 'Price on request',
    available: 'Available',
    reserved: 'Reserved',
    sold: 'Sold',
    details: 'View details',
    backToShop: 'Back to the shop',
    requestToBuy: 'Send a purchase enquiry',
    notFound: 'Instrument not found.',
    year: 'Year',
    model: 'Model',
    category: 'Category',
  },

  home: {
    invite: 'Visit the workshop and take your time playing the instruments.',
  },

  form: {
    heading: 'Write to me',
    name: 'Name',
    email: 'Email address',
    phone: 'Phone number',
    subject: 'Subject',
    message: 'Message',
    captcha: 'Please solve',
    captchaHint: 'A small sum keeps out automated spam.',
    pickTimeFirst: 'You have not chosen a time yet. Please pick one - or hide the appointment picker.',
    send: 'Send',
    sending: 'Sending …',
    required: 'Required',
    success: 'Thank you! We have received your enquiry.',
    error: 'Something went wrong. Please try again.',
    topicLegend: 'What is it about?',
    topicHint: 'Optional - helps us reply to you faster.',
    topics: {
      repair: 'Repair',
      appraisal: 'Appraisal & insurance',
      purchase: 'Purchase interest',
      other: 'Other',
    },
    photos: 'Attach photos (optional)',
    photosHint: 'Up to 3 images - e.g. of the damaged instrument or bow.',
    photoAdd: 'Choose photo',
    photoUploading: 'Uploading photo …',
    photoRemove: 'Remove photo',
    photoError: 'The image could not be uploaded.',
    scheduleToggle: 'Arrange a workshop visit',
    scheduleToggleHint:
      'Optional - only if you would like to come by. A simple question can be sent without an appointment.',
    scheduleHide: 'No appointment needed - hide',
    preferredTimes: 'Preferred times',
    preferredTimesHint: 'Pick up to {n} preferred times - we will confirm one of them by email.',
    pickSlotHint: 'Pick a free slot - it is booked for you right away.',
    removeTime: 'Remove',
    pickSlot: 'Choose an available time',
    pickDayFirst: 'Please choose a day on the left.',
    maxReached: 'Maximum reached - tap a time to remove it.',
    noSlots: 'No times are available on this day.',
    calendarUnavailable:
      'The booking calendar is currently unreachable. Please suggest preferred times instead.',
    loadingSlots: 'Loading available times …',
    suggestInstead: 'Nothing suitable? Suggest your own time',
    showLiveInstead: 'Back to the available slots',
    book: 'Confirm booking',
    bookingSuccess: 'Thank you! Your appointment is confirmed. You will receive an email.',
  },

  gallery: {
    close: 'Close',
    previous: 'Previous image',
    next: 'Next image',
    imageCounter: 'Image {i} of {n}',
  },

  scheduler: {
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
  },

  wizard: {
    eyebrow: 'Purchase enquiry',
    steps: ['Preferred time', 'Your details', 'Send'],
    stepOf: 'Step {i} of {n}',
    terminHint:
      'Would you like to play the instrument at the workshop? Pick a day and suitable times - or simply skip this step.',
    terminHintLive:
      'Choose a free slot for your workshop visit - it is reserved for you right away.',
    skip: 'Continue without a preferred time',
    contactHint: 'How may we reach you?',
    messageLabel: 'Your message (optional)',
    messagePlaceholder: 'Questions about the instrument, the price, directions …',
    summaryHint: 'Please check your details - then send the enquiry.',
    summaryProduct: 'Instrument',
    summaryTimes: 'Preferred times',
    summarySlot: 'Chosen slot',
    summaryContact: 'Contact',
    summaryMessage: 'Message',
    noTimes: 'No preferred time - we will get back to you by email.',
    back: 'Back',
    next: 'Continue',
    send: 'Send enquiry',
    book: 'Confirm booking',
    contactInvalid: 'Please enter your name and a valid email address.',
    successTitle: 'Thank you!',
    close: 'Close',
  },

  notFound: {
    title: 'Page not found',
    body: 'The page you requested does not exist.',
    home: 'Go to the homepage',
  },
  loading: 'Loading …',

  meta: {
    home: {
      title: 'Zvi Dori | Geigenbaumeister',
      description:
        'Zvi Dori, master violin maker in Hannover. New master instruments, custom builds after historic models, Baroque instruments and viols.',
    },
    uebermich: {
      title: 'About me | zvidori',
      description:
        'Zvi Dori has run his own violin-making workshop in Hannover since 2004 - master craftsman in the old Italian tradition.',
      heading: 'About me',
    },
    klang: {
      title: 'Timbre | zvidori',
      description:
        'The sound ideal of the old Italian masters - Stradivari, Amati, Guarneri - guides every instrument built in the workshop.',
      heading: 'Timbre',
    },
    galerie: {
      title: 'Galery | zvidori',
      description:
        'Instruments built in the workshop of Zvi Dori, master violin maker in Hannover.',
    },
    shop: {
      description:
        'Master-crafted violins, violas, cellos and bows from the workshop of Zvi Dori in Hannover.',
    },
    service: {
      title: 'Repairs & Services | zvidori',
      description:
        'Repairs, restoration, bow service, sound adjustment and insurance appraisals for string instruments - workshop of Zvi Dori, master violin maker in Hannover.',
      heading: 'Repairs & Services',
    },
    kontakt: {
      title: 'Contact | zvidori',
      description:
        'Arrange an appointment at the workshop of Zvi Dori, master violin maker in Hannover.',
      heading: 'Contact',
    },
  },
}

const ja: Strings = {
  nav: {
    uebermich: '私について',
    klang: '音色',
    galerie: 'ギャラリー',
    shop: 'ショップ',
    service: 'サービス',
    kontakt: 'お問い合わせ',
  },
  skipToContent: '本文へスキップ',
  menu: 'メニュー',
  closeMenu: 'メニューを閉じる',
  language: '言語',

  footer: {
    privacy: 'プライバシーポリシー',
    imprint: '運営者情報',
    follow: 'フォローする',
  },

  credits: {
    originalSite: '旧ウェブサイトの企画・デザイン（2023年〜2026年）',
  },

  consent: {
    title: 'Cookie設定',
    body: '当サイトの利用状況を把握するため、Google Analyticsの使用を希望しています。その際、お客様の端末にCookieが保存され、データがGoogleに送信されます - ご同意いただいた場合のみです。設定はページ下部からいつでも変更できます。',
    more: 'プライバシーポリシー',
    accept: '同意する',
    decline: '同意しない',
    settings: 'Cookie設定',
  },

  map: {
    title: 'アクセス',
    load: '地図を読み込む',
    note: '地図はGoogleから読み込まれます。その際、お客様のIPアドレスと端末に関する情報がGoogleに送信されます。クリックしない限り、送信は行われません。',
    more: 'プライバシーポリシー',
    directions: 'ルートを検索',
    frameTitle: 'Googleマップ: {address}',
  },

  shop: {
    title: 'ショップ',
    eyebrow: '楽器',
    intro:
      '楽器はオンラインではご購入いただけません。お問い合わせのうえ、工房でじっくりと試奏してください。',
    allCategories: 'すべて',
    empty: '現在、掲載中の楽器はありません。',
    priceOnRequest: '価格はお問い合わせください',
    available: '販売中',
    reserved: 'ご予約済み',
    sold: '売約済み',
    details: '詳細を見る',
    backToShop: 'ショップに戻る',
    requestToBuy: '購入のお問い合わせ',
    notFound: '楽器が見つかりませんでした。',
    year: '製作年',
    model: 'モデル',
    category: 'カテゴリー',
  },

  home: {
    invite: '工房にお越しいただき、ゆっくりと楽器をお試しください。',
  },

  form: {
    heading: 'お問い合わせフォーム',
    name: 'お名前',
    email: 'メールアドレス',
    phone: '電話番号',
    subject: '件名',
    message: 'メッセージ',
    captcha: '次の計算にお答えください',
    captchaHint: '自動送信されるスパムを防ぐための簡単な計算です。',
    pickTimeFirst: 'まだ時間が選ばれていません。時間を選ぶか、予約の選択を閉じてください。',
    send: '送信',
    sending: '送信中…',
    required: '必須',
    success: 'お問い合わせありがとうございます。メッセージを受け付けました。',
    error: 'エラーが発生しました。もう一度お試しください。',
    topicLegend: 'ご用件は何ですか？',
    topicHint: '任意 - より早い回答の参考になります。',
    topics: {
      repair: '修理',
      appraisal: '鑑定・保険',
      purchase: '購入希望',
      other: 'その他',
    },
    photos: '写真を添付（任意）',
    photosHint: '最大3枚 - 破損した楽器や弓の写真など。',
    photoAdd: '写真を選択',
    photoUploading: '写真をアップロード中…',
    photoRemove: '写真を削除',
    photoError: '画像をアップロードできませんでした。',
    scheduleToggle: '工房訪問の予約をする',
    scheduleToggleHint:
      '任意 - ご来訪をご希望の場合のみ。簡単なご質問は予約なしでも送れます。',
    scheduleHide: '予約はしない - 閉じる',
    preferredTimes: 'ご希望の日時',
    preferredTimesHint:
      'ご希望の日時を最大{n}件お選びください - メールでいずれかを確定します。',
    pickSlotHint: '空いている時間を選んでください - その場で予約が確定します。',
    removeTime: '削除',
    pickSlot: '空き時間を選ぶ',
    pickDayFirst: '左側から日付を選択してください。',
    maxReached: '上限に達しました - 削除するには日時をタップしてください。',
    noSlots: 'この日は空きがありません。',
    calendarUnavailable:
      '予約カレンダーに現在接続できません。ご希望の日時をご提案ください。',
    loadingSlots: '空き時間を読み込み中…',
    suggestInstead: 'ご都合の合う時間がない場合は、希望日時をご提案ください',
    showLiveInstead: '空き時間の一覧に戻る',
    book: '予約を確定する',
    bookingSuccess: 'ありがとうございます。ご予約が確定しました。確認メールをお送りします。',
  },

  gallery: {
    close: '閉じる',
    previous: '前の画像',
    next: '次の画像',
    imageCounter: '{n}枚中{i}枚目',
  },

  scheduler: {
    prevMonth: '前の月',
    nextMonth: '次の月',
  },

  wizard: {
    eyebrow: '購入のお問い合わせ',
    steps: ['ご希望日時', 'お客様情報', '送信'],
    stepOf: 'ステップ {i} / {n}',
    terminHint:
      '工房で楽器を試奏なさいますか？ご都合の良い日と時間帯をお選びください - このステップは省略もできます。',
    terminHintLive:
      '工房訪問の空き時間をお選びください - その場でご予約が確定します。',
    skip: '日時を指定せずに進む',
    contactHint: 'ご連絡先を教えてください。',
    messageLabel: 'メッセージ（任意）',
    messagePlaceholder: '楽器や価格、アクセスについてのご質問など…',
    summaryHint: 'ご入力内容をご確認のうえ、お問い合わせを送信してください。',
    summaryProduct: '楽器',
    summaryTimes: 'ご希望の日時',
    summarySlot: '選択した日時',
    summaryContact: 'ご連絡先',
    summaryMessage: 'メッセージ',
    noTimes: '日時の指定なし - メールでご連絡いたします。',
    back: '戻る',
    next: '次へ',
    send: '送信する',
    book: '予約を確定する',
    contactInvalid: 'お名前と有効なメールアドレスを入力してください。',
    successTitle: 'ありがとうございます！',
    close: '閉じる',
  },

  notFound: {
    title: 'ページが見つかりません',
    body: 'お探しのページは存在しません。',
    home: 'ホームへ戻る',
  },
  loading: '読み込み中…',

  meta: {
    home: {
      title: 'Zvi Dori | ヴァイオリン製作マイスター',
      description:
        'ハノーファーのヴァイオリン製作マイスター、Zvi Dori。新作マイスター楽器、歴史的名器に基づく特注製作、バロック楽器とガンバ。',
    },
    uebermich: {
      title: '私について | zvidori',
      description:
        'Zvi Doriは2004年からハノーファーで自身のヴァイオリン工房を営むマイスター - 古いイタリアの伝統を受け継ぎます。',
      heading: '私について',
    },
    klang: {
      title: '音色 | zvidori',
      description:
        'ストラディヴァリ、アマティ、グァルネリ - 古いイタリアの名匠の音の理想が、工房のすべての新作を導きます。',
      heading: '音色',
    },
    galerie: {
      title: 'ギャラリー | zvidori',
      description:
        'ハノーファーのヴァイオリン製作マイスター、Zvi Doriの工房で製作された楽器。',
    },
    shop: {
      description:
        'ハノーファーのZvi Dori工房によるマイスター楽器 - ヴァイオリン、ヴィオラ、チェロ、弓。',
    },
    service: {
      title: '修理・サービス | zvidori',
      description:
        '弦楽器の修理、修復、弓のメンテナンス、音の調整、鑑定 - ハノーファーのヴァイオリン製作マイスターZvi Doriの工房。',
      heading: '修理・サービス',
    },
    kontakt: {
      title: 'お問い合わせ | zvidori',
      description:
        'ハノーファーのヴァイオリン製作マイスターZvi Doriの工房への訪問予約。',
      heading: 'お問い合わせ',
    },
  },
}

const ko: Strings = {
  nav: {
    uebermich: '소개',
    klang: '음색',
    galerie: '갤러리',
    shop: '숍',
    service: '서비스',
    kontakt: '문의',
  },
  skipToContent: '본문으로 건너뛰기',
  menu: '메뉴',
  closeMenu: '메뉴 닫기',
  language: '언어',

  footer: {
    privacy: '개인정보 처리방침',
    imprint: '법적 고지',
    follow: '팔로우',
  },

  credits: {
    originalSite: '이전 웹사이트의 기획 및 디자인 (2023년~2026년)',
  },

  consent: {
    title: '쿠키 설정',
    body: '이 웹사이트가 어떻게 이용되는지 파악하기 위해 Google Analytics를 사용하고자 합니다. 이를 위해 귀하의 기기에 쿠키가 저장되고 데이터가 Google로 전송됩니다 - 동의하신 경우에만 해당합니다. 페이지 하단에서 언제든지 결정을 변경하실 수 있습니다.',
    more: '개인정보 처리방침',
    accept: '동의',
    decline: '거부',
    settings: '쿠키 설정',
  },

  map: {
    title: '찾아오시는 길',
    load: '지도 불러오기',
    note: '지도는 Google에서 불러옵니다. 이때 IP 주소와 기기 정보가 Google로 전송됩니다. 클릭하지 않으면 전송되지 않습니다.',
    more: '개인정보 처리방침',
    directions: '길찾기',
    frameTitle: 'Google 지도: {address}',
  },

  shop: {
    title: '숍',
    eyebrow: '악기',
    intro:
      '악기는 온라인으로 구매하실 수 없습니다. 문의를 보내신 후 공방에서 여유롭게 시연해 보세요.',
    allCategories: '전체',
    empty: '현재 등록된 악기가 없습니다.',
    priceOnRequest: '가격 문의',
    available: '판매 중',
    reserved: '예약됨',
    sold: '판매 완료',
    details: '자세히 보기',
    backToShop: '숍으로 돌아가기',
    requestToBuy: '구매 문의 보내기',
    notFound: '악기를 찾을 수 없습니다.',
    year: '제작 연도',
    model: '모델',
    category: '카테고리',
  },

  home: {
    invite: '공방에 오셔서 여유롭게 악기를 연주해 보세요.',
  },

  form: {
    heading: '문의 보내기',
    name: '이름',
    email: '이메일 주소',
    phone: '전화번호',
    subject: '제목',
    message: '메시지',
    captcha: '다음 계산을 풀어 주세요',
    captchaHint: '자동 스팸 메시지를 막기 위한 간단한 계산입니다.',
    pickTimeFirst: '아직 시간을 선택하지 않으셨습니다. 시간을 선택하시거나 예약 선택을 닫아 주세요.',
    send: '보내기',
    sending: '보내는 중…',
    required: '필수',
    success: '감사합니다! 문의가 접수되었습니다.',
    error: '오류가 발생했습니다. 다시 시도해 주세요.',
    topicLegend: '어떤 용건이신가요?',
    topicHint: '선택 사항 - 더 빠른 답변에 도움이 됩니다.',
    topics: {
      repair: '수리',
      appraisal: '감정·보험',
      purchase: '구매 문의',
      other: '기타',
    },
    photos: '사진 첨부 (선택)',
    photosHint: '최대 3장 - 예: 손상된 악기나 활 사진.',
    photoAdd: '사진 선택',
    photoUploading: '사진 업로드 중…',
    photoRemove: '사진 삭제',
    photoError: '이미지를 업로드할 수 없습니다.',
    scheduleToggle: '공방 방문 예약하기',
    scheduleToggleHint:
      '선택 사항 - 방문을 원하실 때만. 간단한 질문은 예약 없이 보내실 수 있습니다.',
    scheduleHide: '예약 안 함 - 숨기기',
    preferredTimes: '희망 시간',
    preferredTimesHint:
      '희망 시간을 최대 {n}개 선택해 주세요 - 그중 하나를 이메일로 확정해 드립니다.',
    pickSlotHint: '빈 시간을 선택하세요 - 즉시 예약이 확정됩니다.',
    removeTime: '삭제',
    pickSlot: '가능한 시간 선택',
    pickDayFirst: '왼쪽에서 날짜를 선택해 주세요.',
    maxReached: '최대 개수에 도달했습니다 - 삭제하려면 시간을 탭하세요.',
    noSlots: '이 날에는 가능한 시간이 없습니다.',
    calendarUnavailable:
      '예약 캘린더에 현재 접속할 수 없습니다. 대신 희망 시간을 제안해 주세요.',
    loadingSlots: '가능한 시간 불러오는 중…',
    suggestInstead: '맞는 시간이 없나요? 직접 제안하기',
    showLiveInstead: '가능한 시간 목록으로 돌아가기',
    book: '예약 확정',
    bookingSuccess: '감사합니다! 예약이 확정되었습니다. 확인 이메일을 보내드립니다.',
  },

  gallery: {
    close: '닫기',
    previous: '이전 이미지',
    next: '다음 이미지',
    imageCounter: '{n}장 중 {i}번째',
  },

  scheduler: {
    prevMonth: '이전 달',
    nextMonth: '다음 달',
  },

  wizard: {
    eyebrow: '구매 문의',
    steps: ['희망 시간', '연락처 정보', '보내기'],
    stepOf: '{n}단계 중 {i}단계',
    terminHint:
      '공방에서 악기를 시연해 보시겠어요? 날짜와 시간대를 선택하세요 - 이 단계는 건너뛰어도 됩니다.',
    terminHintLive: '공방 방문을 위한 빈 시간을 선택하세요 - 바로 예약됩니다.',
    skip: '희망 시간 없이 계속',
    contactHint: '어떻게 연락드리면 될까요?',
    messageLabel: '메시지 (선택)',
    messagePlaceholder: '악기, 가격, 오시는 길에 대한 질문 등…',
    summaryHint: '입력하신 내용을 확인한 후 문의를 보내 주세요.',
    summaryProduct: '악기',
    summaryTimes: '희망 시간',
    summarySlot: '선택한 시간',
    summaryContact: '연락처',
    summaryMessage: '메시지',
    noTimes: '희망 시간 없음 - 이메일로 연락드리겠습니다.',
    back: '뒤로',
    next: '다음',
    send: '문의 보내기',
    book: '예약 확정',
    contactInvalid: '이름과 유효한 이메일 주소를 입력해 주세요.',
    successTitle: '감사합니다!',
    close: '닫기',
  },

  notFound: {
    title: '페이지를 찾을 수 없습니다',
    body: '요청하신 페이지가 존재하지 않습니다.',
    home: '홈으로 가기',
  },
  loading: '불러오는 중…',

  meta: {
    home: {
      title: 'Zvi Dori | 바이올린 제작 마이스터',
      description:
        '하노버의 바이올린 제작 마이스터 Zvi Dori. 새 마이스터 악기, 역사적 명기를 본뜬 맞춤 제작, 바로크 악기와 비올.',
    },
    uebermich: {
      title: '소개 | zvidori',
      description:
        'Zvi Dori는 2004년부터 하노버에서 자신의 바이올린 공방을 운영합니다 - 옛 이탈리아 전통의 마이스터.',
      heading: '소개',
    },
    klang: {
      title: '음색 | zvidori',
      description:
        '스트라디바리, 아마티, 과르네리 - 옛 이탈리아 명장들의 소리 이상이 공방의 모든 새 악기를 이끕니다.',
      heading: '음색',
    },
    galerie: {
      title: '갤러리 | zvidori',
      description: '하노버의 바이올린 제작 마이스터 Zvi Dori 공방에서 제작된 악기들.',
    },
    shop: {
      description: '하노버 Zvi Dori 공방의 마이스터 악기 - 바이올린, 비올라, 첼로, 활.',
    },
    service: {
      title: '수리·서비스 | zvidori',
      description:
        '현악기 수리, 복원, 활 관리, 소리 조정, 감정 - 하노버의 바이올린 제작 마이스터 Zvi Dori의 공방.',
      heading: '수리 & 서비스',
    },
    kontakt: {
      title: '문의 | zvidori',
      description: '하노버의 바이올린 제작 마이스터 Zvi Dori 공방 방문 예약.',
      heading: '문의',
    },
  },
}

const zh: Strings = {
  nav: {
    uebermich: '关于我',
    klang: '音色',
    galerie: '作品集',
    shop: '商店',
    service: '服务',
    kontakt: '联系',
  },
  skipToContent: '跳到正文',
  menu: '菜单',
  closeMenu: '关闭菜单',
  language: '语言',

  footer: {
    privacy: '隐私政策',
    imprint: '法律声明',
    follow: '关注',
  },

  credits: {
    originalSite: '原网站的策划与设计（2023年至2026年）',
  },

  consent: {
    title: 'Cookie 设置',
    body: '我们希望使用 Google Analytics 了解本网站的使用情况。为此会在您的设备上存储 Cookie 并将数据传输给 Google - 仅在您同意的情况下。您可以随时在页面底部更改您的决定。',
    more: '隐私政策',
    accept: '同意',
    decline: '拒绝',
    settings: 'Cookie 设置',
  },

  map: {
    title: '交通位置',
    load: '加载地图',
    note: '地图由 Google 加载。加载时，您的 IP 地址和设备信息会被传输至 Google。未经您点击，不会发生任何传输。',
    more: '隐私政策',
    directions: '规划路线',
    frameTitle: 'Google 地图：{address}',
  },

  shop: {
    title: '商店',
    eyebrow: '乐器',
    intro: '乐器无法在线购买。请发送咨询，然后到工作室从容试奏。',
    allCategories: '全部',
    empty: '目前没有在售乐器。',
    priceOnRequest: '价格面议',
    available: '在售',
    reserved: '已预订',
    sold: '已售出',
    details: '查看详情',
    backToShop: '返回商店',
    requestToBuy: '发送购买咨询',
    notFound: '未找到该乐器。',
    year: '制作年份',
    model: '型号',
    category: '类别',
  },

  home: {
    invite: '欢迎来到工作室，从容试奏各件乐器。',
  },

  form: {
    heading: '给我写信',
    name: '姓名',
    email: '电子邮箱',
    phone: '电话号码',
    subject: '主题',
    message: '留言',
    captcha: '请计算',
    captchaHint: '一道简单的算术题，用于防止自动发送的垃圾信息。',
    pickTimeFirst: '您还没有选择时间。请选择一个时间，或收起预约选择。',
    send: '发送',
    sending: '发送中…',
    required: '必填',
    success: '感谢您的咨询！我们已收到您的留言。',
    error: '发生错误，请重试。',
    topicLegend: '您想咨询什么？',
    topicHint: '选填 - 有助于我们更快回复您。',
    topics: {
      repair: '维修',
      appraisal: '鉴定与保险',
      purchase: '购买意向',
      other: '其他',
    },
    photos: '附加照片（选填）',
    photosHint: '最多3张 - 例如受损乐器或琴弓的照片。',
    photoAdd: '选择照片',
    photoUploading: '照片上传中…',
    photoRemove: '删除照片',
    photoError: '图片上传失败。',
    scheduleToggle: '预约到访工作室',
    scheduleToggleHint: '选填 - 仅在您想来访时。简单的问题无需预约即可发送。',
    scheduleHide: '不需要预约 - 收起',
    preferredTimes: '期望时间',
    preferredTimesHint: '最多选择{n}个期望时间 - 我们将通过邮件确认其中一个。',
    pickSlotHint: '选择一个空闲时段 - 立即为您确认预约。',
    removeTime: '删除',
    pickSlot: '选择可用时间',
    pickDayFirst: '请先在左侧选择日期。',
    maxReached: '已达上限 - 点击某个时间可将其删除。',
    noSlots: '当天没有可用时间。',
    calendarUnavailable: '预约日历暂时无法访问，请改为提议期望时间。',
    loadingSlots: '正在加载可用时间…',
    suggestInstead: '没有合适的时间？自行提议时间',
    showLiveInstead: '返回可用时间列表',
    book: '确认预约',
    bookingSuccess: '谢谢！您的预约已确认，我们将向您发送确认邮件。',
  },

  gallery: {
    close: '关闭',
    previous: '上一张',
    next: '下一张',
    imageCounter: '第{i}张，共{n}张',
  },

  scheduler: {
    prevMonth: '上个月',
    nextMonth: '下个月',
  },

  wizard: {
    eyebrow: '购买咨询',
    steps: ['期望时间', '您的信息', '发送'],
    stepOf: '第{i}步，共{n}步',
    terminHint:
      '想到工作室试奏这件乐器吗？选择日期和合适的时间段 - 也可以直接跳过此步骤。',
    terminHintLive: '为您的工作室之行选择一个空闲时段 - 立即为您保留。',
    skip: '不选时间，继续',
    contactHint: '我们如何与您联系？',
    messageLabel: '您的留言（选填）',
    messagePlaceholder: '关于乐器、价格、交通的问题……',
    summaryHint: '请核对您的信息 - 然后发送咨询。',
    summaryProduct: '乐器',
    summaryTimes: '期望时间',
    summarySlot: '所选时段',
    summaryContact: '联系方式',
    summaryMessage: '留言',
    noTimes: '未选择时间 - 我们将通过邮件与您联系。',
    back: '返回',
    next: '继续',
    send: '发送咨询',
    book: '确认预约',
    contactInvalid: '请输入您的姓名和有效的电子邮箱。',
    successTitle: '谢谢！',
    close: '关闭',
  },

  notFound: {
    title: '页面未找到',
    body: '您访问的页面不存在。',
    home: '返回首页',
  },
  loading: '加载中…',

  meta: {
    home: {
      title: 'Zvi Dori | 小提琴制作大师',
      description:
        '汉诺威小提琴制作大师 Zvi Dori。全新大师级乐器、依照历史名琴定制，以及巴洛克乐器与维奥尔琴。',
    },
    uebermich: {
      title: '关于我 | zvidori',
      description:
        'Zvi Dori 自2004年起在汉诺威经营自己的小提琴制作工作室 - 传承意大利古老传统的制琴大师。',
      heading: '关于我',
    },
    klang: {
      title: '音色 | zvidori',
      description:
        '斯特拉迪瓦里、阿玛蒂、瓜奈利 - 意大利古代名家的音色理想引领工作室的每一件新作。',
      heading: '音色',
    },
    galerie: {
      title: '作品集 | zvidori',
      description: '汉诺威小提琴制作大师 Zvi Dori 工作室制作的乐器。',
    },
    shop: {
      description: '来自汉诺威 Zvi Dori 工作室的大师级乐器 - 小提琴、中提琴、大提琴与琴弓。',
    },
    service: {
      title: '维修与服务 | zvidori',
      description:
        '弦乐器维修、修复、琴弓保养、音色调整与鉴定 - 汉诺威小提琴制作大师 Zvi Dori 的工作室。',
      heading: '维修与服务',
    },
    kontakt: {
      title: '联系 | zvidori',
      description: '预约到访汉诺威小提琴制作大师 Zvi Dori 的工作室。',
      heading: '联系',
    },
  },
}

const strings: Record<Locale, Strings> = { de, en, ja, ko, zh }

export function t(locale: Locale): Strings {
  return strings[locale]
}

/** Replace {placeholders} in a template string. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? String(values[key]) : match,
  )
}
